import { useRef, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "/api";
const emptyAddress = { street: "", postal_code: "", city: "", country: "Deutschland" };

export default function AuthorizedRecipientHub({ locale = "de", eligibility, defaults, services, authToken, onChange, onDownload, onStatus }) {
  const en = locale === "en";
  const t = (de, english) => (en ? english : de);
  const [formOpen, setFormOpen] = useState(false);
  const [signing, setSigning] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [signingMode, setSigningMode] = useState("choose");
  const [uploadFiles, setUploadFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    locale,
    customer_kind: defaults?.principal_name ? "company" : "person",
    principal_name: defaults?.principal_name || "",
    principal_address: defaults?.principal_address || emptyAddress,
    legal_representative: defaults?.legal_representative || "",
    register_name: "",
    register_number: "",
    authority_type: "tax_office",
    authority_name: "",
    authority_address: emptyAddress,
    principal_phone: "",
    tax_identifier: "",
    authority_reference: "",
    scope_text: "",
    procedure_scope: "",
    procedure_scope_confirmed: false,
    valid_from: new Date().toISOString().slice(0, 10),
    validity_mode: "subscription",
    valid_until: "",
    consent_legal_effect: false,
    consent_opening: false,
    consent_unchanged_document: false,
  });
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const clearFieldError = (key) =>
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  const setField = (key, value) => {
    clearFieldError(key);
    setForm((current) => ({ ...current, [key]: value }));
  };
  const setAddress = (key, field, value) => {
    clearFieldError(`${key}.${field}`);
    setForm((current) => ({ ...current, [key]: { ...current[key], [field]: value } }));
  };
  const validateForm = () => {
    const missing = {};
    const required = (key, value) => {
      if (!String(value || "").trim()) missing[key] = true;
    };
    required("principal_name", form.principal_name);
    ["street", "postal_code", "city", "country"].forEach((field) => required(`principal_address.${field}`, form.principal_address?.[field]));
    required("authority_name", form.authority_name);
    ["street", "postal_code", "city", "country"].forEach((field) => required(`authority_address.${field}`, form.authority_address?.[field]));
    required("scope_text", form.scope_text);
    if (form.customer_kind === "company") {
      required("legal_representative", form.legal_representative);
      required("register", form.register_name);
      required("register", form.register_number);
    }
    if (form.authority_type === "tax_office" && !form.procedure_scope) missing.procedure_scope = true;
    if (form.authority_type === "tax_office" && !form.procedure_scope_confirmed) missing.procedure_scope_confirmed = true;
    if (form.validity_mode === "fixed_date") required("valid_until", form.valid_until);
    if (!form.consent_legal_effect) missing.consent_legal_effect = true;
    if (!form.consent_opening) missing.consent_opening = true;
    if (!form.consent_unchanged_document) missing.consent_unchanged_document = true;
    return missing;
  };
  const request = async (url, options = {}) => {
    const response = await fetch(`${API_URL}${url}`, { ...options, headers: { Authorization: `Bearer ${authToken || ""}`, "Content-Type": "application/json", ...(options.headers || {}) } });
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401) throw new Error(t("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.", "Your session has expired. Please sign in again."));
    if (!response.ok) throw new Error(payload.error || t("Vorgang fehlgeschlagen.", "Request failed."));
    return payload;
  };
  const create = async () => {
    setError("");
    const missing = validateForm();
    setFieldErrors(missing);
    if (Object.keys(missing).length) {
      setError(t("Bitte prüfen Sie die rot markierten Pflichtfelder und Bestätigungen.", "Please review the required fields and confirmations marked in red."));
      window.setTimeout(() => document.querySelector('.recipient-dialog [aria-invalid="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      return;
    }
    setBusy(true);
    try {
      const payload = await request("/customer/authorized-recipient-services", { method: "POST", body: JSON.stringify({ ...form, legal_entity_id: defaults?.legal_entity_id || null }) });
      onChange([payload.service, ...services]);
      setFieldErrors({});
      setFormOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const pointer = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    const x = ((event.clientX - rect.left) * canvas.width) / rect.width;
    const y = ((event.clientY - rect.top) * canvas.height) / rect.height;
    if (event.type === "pointerdown") {
      drawing.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
      canvas.setPointerCapture(event.pointerId);
    } else if (event.type === "pointermove" && drawing.current) {
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#071f3d";
      ctx.lineTo(x, y);
      ctx.stroke();
      setHasSignature(true);
    } else drawing.current = false;
  };
  const openSign = async (service) => {
    setError("");
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/customer/authorized-recipient-services/${service.id}/documents/authorization?lang=${locale}`, { headers: { Authorization: `Bearer ${authToken || ""}` } });
      if (response.status === 401) throw new Error(t("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.", "Your session has expired. Please sign in again."));
      if (!response.ok) throw new Error(t("Vorschau konnte nicht geladen werden.", "Preview could not be loaded."));
      const url = URL.createObjectURL(await response.blob());
      setPreviewUrl(url);
      setHasSignature(false);
      setSigningMode("choose");
      setSigning(service);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const closeSign = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setSigning(null);
    setSigningMode("choose");
    setHasSignature(false);
  };
  const sign = async () => {
    setBusy(true);
    setError("");
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      const payload = await request(`/customer/authorized-recipient-services/${signing.id}/sign`, { method: "POST", body: JSON.stringify({ signature, accuracy_confirmed: true, language: locale }) });
      onChange(services.map((item) => (item.id === signing.id ? payload.service : item)));
      closeSign();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const choosePrint = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = await request(`/customer/authorized-recipient-services/${signing.id}/choose-print-signature`, { method: "POST", body: JSON.stringify({ language: locale }) });
      onChange(services.map((item) => (item.id === signing.id ? payload.service : item)));
      onDownload?.(payload.service, "authorization");
      closeSign();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const uploadHandwritten = async (service) => {
    const file = uploadFiles[service.id];
    if (!file) {
      setError(t("Bitte wählen Sie zuerst eine PDF-, JPG- oder PNG-Datei aus.", "Please select a PDF, JPG or PNG file first."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("signed_authorization", file);
      const response = await fetch(`${API_URL}/customer/authorized-recipient-services/${service.id}/upload-handwritten-signature`, { method: "POST", headers: { Authorization: `Bearer ${authToken || ""}` }, body });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || t("Upload fehlgeschlagen.", "Upload failed."));
      onChange(services.map((item) => (item.id === service.id ? payload.service : item)));
      setUploadFiles((current) => {
        const next = { ...current };
        delete next[service.id];
        return next;
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (eligibility?.pilot_access === false) return null;

  return (
    <section className="recipient-hub">
      <div className="customer-section-heading">
        <div>
          <span className="eyebrow">{t("BEHÖRDLICHER EMPFANGSSERVICE", "AUTHORISED RECIPIENT SERVICE")}</span>
          <h2>{t("Empfangsvollmacht für Behördenpost", "Authorisation to receive official authority mail")}</h2>
          <p>{t("Erstellen Sie für jede Behörde oder Stelle eine eigene, auf den gewählten Vorgang beschränkte Empfangsvollmacht.", "Create a separate receipt authorisation for each authority or office, limited to the selected matter.")}</p>
          <p>{t("YourGermanAddress empfängt und digitalisiert die Korrespondenz im Rahmen Ihres Tarifs. Wir leisten keine Steuer- oder Rechtsberatung und bearbeiten keine Fristen oder Schreiben für Sie.", "YourGermanAddress receives and digitises correspondence under your plan. We do not provide tax or legal advice and do not process deadlines or correspondence for you.")}</p>
        </div>
        <button
          className="primary-button"
          type="button"
          disabled={!eligibility?.eligible}
          onClick={() => {
            setError("");
            setFormOpen(true);
          }}
        >
          {t("Neue Vollmacht erstellen", "Create new authorisation")}
        </button>
      </div>
      {!eligibility?.eligible ? <div className="warning-box">{t("Verfügbar nach vollständiger Verifizierung mit einem aktiven Geschäftsadresstarif.", "Available after full verification with an active business address plan.")}</div> : null}
      {error && !formOpen && !signing ? <div className="warning-box">{error}</div> : null}
      {formOpen ? (
        <div className="recipient-modal">
          <div className="recipient-dialog">
            <h2>{t("Angaben zur Vollmacht", "Authorisation details")}</h2>
            {error ? (
              <div className="recipient-validation-summary" role="alert">
                <strong>{t("Eingaben unvollständig", "Incomplete information")}</strong>
                <span>{error}</span>
              </div>
            ) : null}
            <label>
              {t("Kunde / Firma", "Principal / company")}
              <input aria-invalid={fieldErrors.principal_name || undefined} value={form.principal_name} onChange={(e) => setField("principal_name", e.target.value)} />
            </label>
            <div className="recipient-grid">
              <label>
                {t("Vertreten durch", "Represented by")}
                <input aria-invalid={fieldErrors.legal_representative || undefined} value={form.legal_representative} onChange={(e) => setField("legal_representative", e.target.value)} />
              </label>
              <label>
                {t("Tagsüber telefonisch erreichbar", "Daytime telephone")}
                <input value={form.principal_phone} onChange={(e) => setField("principal_phone", e.target.value)} />
              </label>
              <label>
                {t("Register und Nummer", "Register and number")}
                <input
                  aria-invalid={fieldErrors.register || undefined}
                  value={`${form.register_name}${form.register_name && form.register_number ? " · " : ""}${form.register_number}`}
                  onChange={(e) => {
                    clearFieldError("register");
                    const [name, number = ""] = e.target.value.split("·");
                    setField("register_name", name.trim());
                    setField("register_number", number.trim());
                  }}
                />
              </label>
            </div>
            <h3>{t("Aktuelle Anschrift", "Current address")}</h3>
            <Address value={form.principal_address} errorPrefix="principal_address" errors={fieldErrors} change={(field, value) => setAddress("principal_address", field, value)} />
            <h3>{t("Behörde oder Stelle", "Authority or office")}</h3>
            <label>
              {t("Behördentyp", "Authority type")}
              <select value={form.authority_type} onChange={(e) => setField("authority_type", e.target.value)}>
                <option value="tax_office">{t("Finanzamt", "Tax office")}</option>
                <option value="trade_office">{t("Gewerbeamt", "Trade office")}</option>
                <option value="vehicle_registration">{t("Zulassungsstelle", "Vehicle registration office")}</option>
                <option value="other">{t("Andere Behörde", "Other authority")}</option>
              </select>
            </label>
            <label>
              {t("Name der Behörde / Stelle", "Authority / office name")}
              <input aria-invalid={fieldErrors.authority_name || undefined} value={form.authority_name} onChange={(e) => setField("authority_name", e.target.value)} />
            </label>
            <Address value={form.authority_address} errorPrefix="authority_address" errors={fieldErrors} change={(field, value) => setAddress("authority_address", field, value)} />
            <div className="recipient-grid">
              <label>
                {t("Kennnummer(n)", "Identifier(s)")}
                <textarea autoComplete="off" value={form.tax_identifier} onChange={(e) => setField("tax_identifier", e.target.value)} placeholder={t("Mehrere Nummern mit Komma oder neuer Zeile trennen", "Separate multiple numbers with commas or new lines")} />
              </label>
              <label>
                {t("Aktenzeichen", "Reference")}
                <input autoComplete="off" value={form.authority_reference} onChange={(e) => setField("authority_reference", e.target.value)} />
              </label>
            </div>
            <label>
              {t("Vorgang / Empfangsumfang", "Matter / receipt scope")}
              <textarea aria-invalid={fieldErrors.scope_text || undefined} value={form.scope_text} onChange={(e) => setField("scope_text", e.target.value)} />
            </label>
            {form.authority_type === "tax_office" ? <fieldset className={`procedure-scope${fieldErrors.procedure_scope || fieldErrors.procedure_scope_confirmed ? " recipient-field-error" : ""}`} aria-invalid={fieldErrors.procedure_scope || fieldErrors.procedure_scope_confirmed || undefined}>
              <legend>{t("Verfahrensumfang – genau eine Option aktiv auswählen", "Procedure scope — actively select exactly one option")}</legend>
              <label className="check-row">
                <input type="radio" name="procedure-scope" checked={form.procedure_scope === "assessment_and_collection"} onChange={() => setField("procedure_scope", "assessment_and_collection")} />
                <span>
                  <strong>{t("Feststellungs-/Festsetzungs- und Erhebungsverfahren", "Assessment and collection proceedings")}</strong>
                  <small>{t("Empfang sämtlicher Schreiben und Bescheide der Finanzverwaltung.", "Receipt of all correspondence and notices from the tax administration.")}</small>
                </span>
              </label>
              <label className="check-row">
                <input type="radio" name="procedure-scope" checked={form.procedure_scope === "assessment_only"} onChange={() => setField("procedure_scope", "assessment_only")} />
                <span>
                  <strong>{t("Feststellungs-/Festsetzungsverfahren", "Assessment proceedings only")}</strong>
                  <small>{t("Schreiben zur Ermittlung und Festsetzung von Besteuerungsgrundlagen, Steuern und Nebenleistungen, einschließlich Steuerbescheiden.", "Correspondence concerning assessment bases, taxes and ancillary tax payments, including tax notices.")}</small>
                </span>
              </label>
              <label className="check-row">
                <input type="radio" name="procedure-scope" checked={form.procedure_scope === "collection_only"} onChange={() => setField("procedure_scope", "collection_only")} />
                <span>
                  <strong>{t("Erhebungsverfahren", "Collection proceedings only")}</strong>
                  <small>{t("Schreiben zu Zahlung, Erstattung und gegebenenfalls Einziehung steuerlicher Beträge.", "Correspondence concerning payment, reimbursement and, where applicable, collection of tax amounts.")}</small>
                </span>
              </label>
              <div className="info-box">{t("Unverbindlicher Produkthinweis: Für einen vollständigen Empfangsservice wird regelmäßig die erste Variante benötigt. Dies ist keine Steuer- oder Rechtsberatung.", "Non-binding product note: the first option is generally required for a complete receipt service. This is not tax or legal advice.")}</div>
              <label className={`check-row${fieldErrors.procedure_scope_confirmed ? " recipient-field-error" : ""}`}>
                <input aria-invalid={fieldErrors.procedure_scope_confirmed || undefined} type="checkbox" checked={form.procedure_scope_confirmed} onChange={(e) => setField("procedure_scope_confirmed", e.target.checked)} />
                {t("Ich habe den Verfahrensumfang selbst gewählt und bestätige diese Auswahl.", "I selected the procedure scope myself and confirm this choice.")}
              </label>
            </fieldset> : null}
            <div className="recipient-grid">
              <label>
                {t("Gültig ab", "Valid from")}
                <input type="date" value={form.valid_from} onChange={(e) => setField("valid_from", e.target.value)} />
              </label>
              <label>
                {t("Gültigkeitsdauer", "Validity")}
                <select value={form.validity_mode} onChange={(e) => setField("validity_mode", e.target.value)}>
                  <option value="subscription">{t("Für die Dauer meines aktiven Abonnements", "For the duration of my active subscription")}</option>
                  <option value="fixed_date">{t("Bis zu einem bestimmten Datum", "Until a specific date")}</option>
                  <option value="until_revoked">{t("Bis zum Widerruf", "Until revoked")}</option>
                </select>
              </label>
              {form.validity_mode === "fixed_date" ? <label>
                {t("Gültig bis", "Valid until")}
                <input aria-invalid={fieldErrors.valid_until || undefined} type="date" min={form.valid_from} value={form.valid_until} onChange={(e) => setField("valid_until", e.target.value)} />
              </label> : null}
            </div>
            <label className={`check-row${fieldErrors.consent_legal_effect ? " recipient-field-error" : ""}`}>
              <input aria-invalid={fieldErrors.consent_legal_effect || undefined} type="checkbox" checked={form.consent_legal_effect} onChange={(e) => setField("consent_legal_effect", e.target.checked)} />
              {t("Ich verstehe, dass der Eingang eines Behördenschreibens bei YourGermanAddress rechtliche Fristen für mich in Gang setzen kann und YourGermanAddress diese Fristen weder prüft noch überwacht.", "I understand that receipt of an official authority letter by YourGermanAddress may start legal deadlines for me and that YourGermanAddress neither reviews nor monitors those deadlines.")}
            </label>
            <label className={`check-row${fieldErrors.consent_opening ? " recipient-field-error" : ""}`}>
              <input aria-invalid={fieldErrors.consent_opening || undefined} type="checkbox" checked={form.consent_opening} onChange={(e) => setField("consent_opening", e.target.checked)} />
              {t("Ich beauftrage das Öffnen und Digitalisieren der erfassten Behördenpost.", "I instruct the opening and digitisation of covered official mail.")}
            </label>
            <label className={`check-row${fieldErrors.consent_unchanged_document ? " recipient-field-error" : ""}`}>
              <input aria-invalid={fieldErrors.consent_unchanged_document || undefined} type="checkbox" checked={form.consent_unchanged_document} onChange={(e) => setField("consent_unchanged_document", e.target.checked)} />
              {t("Ich werde ausschließlich die unveränderte, vom Business Hub erzeugte Fassung verwenden.", "I will use only the unmodified version generated by the Business Hub.")}
            </label>
            <div className="customer-card-actions">
              <button
                className="secondary-button"
                onClick={() => {
                  setError("");
                  setFieldErrors({});
                  setFormOpen(false);
                }}
              >
                {t("Abbrechen", "Cancel")}
              </button>
              <button className="primary-button" disabled={busy} onClick={create}>
                {t("Entwurf anlegen", "Create draft")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {signing ? (
        <div className="recipient-modal">
          <div className="recipient-dialog recipient-signing-dialog">
            <h2>{t("Vollständige Vorschau bestätigen und unterschreiben", "Confirm full preview and sign")}</h2>
            {previewUrl ? <iframe className="recipient-pdf-preview" title={t("Dokumentvorschau", "Document preview")} src={previewUrl} /> : null}
            {error ? <div className="warning-box" role="alert">{error}</div> : null}
            {signingMode === "choose" ? <div className="recipient-signing-options"><button className="primary-button" onClick={() => setSigningMode("digital")}>{t("Jetzt digital unterschreiben", "Sign digitally now")}</button><button className="secondary-button" disabled={busy} onClick={choosePrint}>{t("Ausdrucken und handschriftlich unterschreiben", "Print and sign by hand")}</button></div> : null}
            <canvas ref={canvasRef} className={`signature-pad${signingMode === "digital" ? "" : " recipient-hidden"}`} width="700" height="180" onPointerDown={pointer} onPointerMove={pointer} onPointerUp={pointer} onPointerCancel={pointer} />
            {signingMode === "digital" ? <p>{t("Mit der Unterschrift bestätigen Sie die Richtigkeit der vollständigen Vorschau. Danach wird eine unveränderliche PDF im Business Hub gespeichert.", "By signing, you confirm the complete preview is correct. An immutable PDF is then stored in the Business Hub.")}</p> : null}
            <div className="customer-card-actions">
              <button className="secondary-button" onClick={closeSign}>
                {t("Abbrechen", "Cancel")}
              </button>
              {signingMode === "digital" ? <><button
                className="secondary-button"
                onClick={() => {
                  canvasRef.current.getContext("2d").clearRect(0, 0, 700, 180);
                  setHasSignature(false);
                }}
              >
                {t("Löschen", "Clear")}
              </button>
              <button className="primary-button" disabled={busy || !hasSignature} onClick={sign}>
                {t("Verbindlich unterschreiben", "Sign document")}
              </button></> : null}
            </div>
          </div>
        </div>
      ) : null}
      <div className="customer-dashboard-grid">
        {services.map((service) => (
          <article className="customer-dashboard-card recipient-service-card" key={service.id}>
            <h3>{service.authority_name}</h3>
            <p>{service.scope_text}</p>
            <p><strong>{t("Gültigkeit", "Validity")}:</strong> {service.validity_mode === "subscription" ? t("Dauer des aktiven Abonnements", "Active subscription duration") : service.validity_mode === "fixed_date" ? `${t("bis", "until")} ${service.valid_until}` : t("bis zum Widerruf", "until revoked")}</p>
            <p><strong>{t("Hinweisversion", "Notice version")}:</strong> {service.terms_version || t("Bestandsdokument", "Legacy document")}</p>
            <p>
              <strong>{t("Status", "Status")}:</strong> {service.status === "customer_submitted" ? t("Vom Kunden eingereicht – keine Bestätigung des Finanzamts", "Submitted by customer – not confirmed by the authority") : service.status === "awaiting_print_signature" ? t("Druckfassung gewählt – unterschriebene Fassung ausstehend", "Print version selected – signed version pending") : service.status === "customer_signed" && service.signing_method === "print" ? t("Unterschriebene Fassung hochgeladen", "Signed version uploaded") : service.status === "customer_signed" ? t("Digital unterschrieben", "Digitally signed") : service.status === "revocation_requested" ? t("Widerruf vorgemerkt", "Revocation noted") : t("Entwurf", "Draft")}
            </p>
            {service.status === "awaiting_print_signature" ? <div className="recipient-print-upload"><div className="warning-box">{t("Bitte unterschreiben Sie ausschließlich die unveränderte, vom System erstellte Druckfassung. Nachträgliche Änderungen oder Ergänzungen sind nicht gültig.", "Please sign only the unchanged print version generated by the system. Subsequent changes or additions are not valid.")}</div><p>{t("Bei Wahl der handschriftlichen Unterzeichnung ist ausschließlich die vom Anbieter systemseitig erzeugte, inhaltlich unveränderte Druckfassung zur Unterschrift vorgesehen. Eigenmächtige Änderungen, Streichungen oder Ergänzungen werden nicht Vertragsbestandteil und können zur Unwirksamkeit oder Nichtverwendbarkeit der Vollmacht führen.", "If handwritten signing is selected, only the unaltered print version generated by the provider is intended for signature. Unauthorised changes, deletions or additions do not become part of the agreement and may render the authorisation invalid or unusable.")}</p><label className="recipient-file-label">{t("Unterschriebene Fassung hochladen (PDF, JPG oder PNG, max. 10 MB)", "Upload signed version (PDF, JPG or PNG, max. 10 MB)")}<input type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" onChange={(event) => setUploadFiles((current) => ({ ...current, [service.id]: event.target.files?.[0] || null }))} /></label><button className="primary-button" disabled={busy || !uploadFiles[service.id]} onClick={() => uploadHandwritten(service)}>{t("Unterschriebene Fassung hochladen", "Upload signed version")}</button></div> : null}
            <div className="recipient-document-actions">
              {[
                ["authorization", service.signing_method === "print" ? t("Unveränderte Druckfassung", "Unchanged print version") : t("Vollmacht herunterladen", "Download authorisation")],
                ...(service.handwritten_uploaded_at ? [["signed-upload", t("Hochgeladene unterschriebene Fassung", "Uploaded signed version")]] : []),
                ["submission-letter", t("Anschreiben herunterladen", "Download cover letter")],
                ["revocation", t("Widerruf herunterladen", "Download revocation")],
                ["instructions", t("Anleitung herunterladen", "Download guide")],
              ].map(([type, label]) => (
                <button key={type} className="recipient-document-button" onClick={() => onDownload?.(service, type)}>
                  <span aria-hidden="true">↓</span>
                  {label}
                </button>
              ))}
            </div>
            <div className="recipient-service-actions">
              {["draft_created", "downloaded"].includes(service.status) ? (
                <button className="primary-button" disabled={busy} onClick={() => openSign(service)}>
                  {t("Prüfen & unterschreiben", "Review & sign")}
                </button>
              ) : null}
              {service.status === "customer_signed" ? (
                <button className="primary-button" onClick={() => onStatus?.(service, "customer_submitted")}>
                  {t("Als selbst eingereicht markieren", "Mark as submitted by me")}
                </button>
              ) : null}
              {["customer_signed", "customer_submitted"].includes(service.status) ? (
                <button className="danger-button" onClick={() => onStatus?.(service, "revocation_requested")}>
                  {t("Widerruf vormerken", "Note revocation")}
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Address({ value, change, errors = {}, errorPrefix }) {
  return (
    <div className="recipient-grid">
      <input aria-invalid={errors[`${errorPrefix}.street`] || undefined} placeholder="Straße / Street" value={value.street} onChange={(e) => change("street", e.target.value)} />
      <input aria-invalid={errors[`${errorPrefix}.postal_code`] || undefined} placeholder="PLZ / Postal code" value={value.postal_code} onChange={(e) => change("postal_code", e.target.value)} />
      <input aria-invalid={errors[`${errorPrefix}.city`] || undefined} placeholder="Ort / City" value={value.city} onChange={(e) => change("city", e.target.value)} />
      <input aria-invalid={errors[`${errorPrefix}.country`] || undefined} placeholder="Land / Country" value={value.country} onChange={(e) => change("country", e.target.value)} />
    </div>
  );
}
